import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPinIcon, PhoneIcon, EnvelopeIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useFirestore } from '../hooks/useFirestore';
import { getContactInfo, submitInquiry } from '../firebase/firestore';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import './Contact.css';

const Contact = () => {
    const { t, i18n } = useTranslation();
    const { data: contactInfo, loading: infoLoading } = useFirestore(useCallback(() => getContactInfo(), []));

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const [formStatus, setFormStatus] = useState({
        submitting: false,
        success: false,
        error: null
    });

    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('contact.form.required');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('contact.form.required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('contact.form.invalidEmail');
        }

        if (!formData.phone.trim()) {
            newErrors.phone = t('contact.form.required');
        }

        if (!formData.message.trim()) {
            newErrors.message = t('contact.form.required');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setFormStatus({ submitting: true, success: false, error: null });

        try {
            await submitInquiry({
                ...formData,
                language: i18n.language
            });

            setFormStatus({ submitting: false, success: true, error: null });
            setFormData({ name: '', email: '', phone: '', message: '' });

            // Reset success message after 5 seconds
            setTimeout(() => {
                setFormStatus(prev => ({ ...prev, success: false }));
            }, 5000);
        } catch (error) {
            setFormStatus({
                submitting: false,
                success: false,
                error: error.message || t('contact.form.error')
            });
        }
    };

    return (
        <>
            <Helmet>
                <title>{t('contact.title')} | {t('hero.title')}</title>
                <meta name="description" content={t('contact.subtitle')} />
                <html lang={i18n.language} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} />
            </Helmet>

            <main className="contact-page">
                {/* Page Header */}
                <div className="page-header">
                    <div className="container">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                            <h1>{t('contact.title')}</h1>
                            <div className="accent-line center"></div>
                            <p className="subtitle">{t('contact.subtitle')}</p>
                        </motion.div>
                    </div>
                </div>

                {/* Contact Content */}
                <Section>
                    <div className="contact-grid">
                        {/* Contact Form */}
                        <motion.div
                            className="contact-form-wrapper"
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2>{t('contact.form.submit')}</h2>
                            <form onSubmit={handleSubmit} className="contact-form">
                                <div className="form-group">
                                    <label htmlFor="name">{t('contact.form.name')}</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={errors.name ? 'error' : ''}
                                    />
                                    {errors.name && <span className="error-message">{errors.name}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">{t('contact.form.email')}</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={errors.email ? 'error' : ''}
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">{t('contact.form.phone')}</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={errors.phone ? 'error' : ''}
                                    />
                                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message">{t('contact.form.message')}</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows="5"
                                        value={formData.message}
                                        onChange={handleChange}
                                        className={errors.message ? 'error' : ''}
                                    ></textarea>
                                    {errors.message && <span className="error-message">{errors.message}</span>}
                                </div>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={formStatus.submitting}
                                    className="submit-btn"
                                >
                                    {formStatus.submitting ? t('contact.form.sending') : t('contact.form.submit')}
                                </Button>

                                {formStatus.success && (
                                    <div className="success-message">
                                        {t('contact.form.success')}
                                    </div>
                                )}

                                {formStatus.error && (
                                    <div className="error-message-box">
                                        {formStatus.error}
                                    </div>
                                )}
                            </form>
                        </motion.div>

                        {/* Contact Information */}
                        <motion.div
                            className="contact-info-wrapper"
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2>{t('contact.info.address')}</h2>

                            {infoLoading ? (
                                <Loading />
                            ) : (
                                <div className="contact-info-list">
                                    <div className="info-item">
                                        <MapPinIcon className="info-icon" />
                                        <div>
                                            <h4>{t('contact.info.address')}</h4>
                                            <p>
                                                {i18n.language === 'ar' ? '55 شارع محمد فريد - مصر الجديدة - القاهرة - مصر' : '55 Mohamed Farid St. Heliopolis - Cairo - Egypt'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="info-item">
                                        <PhoneIcon className="info-icon" />
                                        <div>
                                            <h4>{t('contact.info.phone')}</h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                <a href="tel:+201020008449">+201020008449</a>
                                                <a href="tel:+20226438448">+20226438448</a>
                                                <a href="tel:+201007707607">+201007707607</a>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="info-item">
                                        <EnvelopeIcon className="info-icon" />
                                        <div>
                                            <h4>{t('contact.info.email')}</h4>
                                            <a href={`mailto:${contactInfo?.email || 'info@sigmaelevator-eg.com'}`}>
                                                {contactInfo?.email || 'info@sigmaelevator-eg.com'}
                                            </a>
                                        </div>
                                    </div>

                                    <div className="info-item">
                                        <ClockIcon className="info-icon" />
                                        <div>
                                            <h4>{t('contact.info.hours')}</h4>
                                            <p>
                                                {i18n.language === 'ar'
                                                    ? 'الأحد - الخميس: 8:00 ص - 5:00 م'
                                                    : 'Sunday - Thursday: 8:00 AM - 5:00 PM'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Social Media */}
                            <div className="social-section">
                                <h3>{t('contact.info.social')}</h3>
                                <div className="social-links">
                                    <a href="https://www.facebook.com/SIGMAELEVATORS" target="_blank" rel="noopener noreferrer" className="social-link">
                                        <svg className="social-icon" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                        </svg>
                                    </a>
                                    <a href="https://www.instagram.com/sigma_elevator_egypt" target="_blank" rel="noopener noreferrer" className="social-link">
                                        <svg className="social-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                                        </svg>
                                    </a>
                                    <a href="https://www.tiktok.com/@sigma.elevator.eg" target="_blank" rel="noopener noreferrer" className="social-link">
                                        <svg className="social-icon" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </Section>

                {/* Map Section */}
                <Section background="gray-light">
                    <div className="map-container">
                        <iframe
                            title="Google Maps Location"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3451.9837!2d31.3323!3d30.103!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14583e2545d6259d%3A0x6bba5229ca23d242!2sHeliopolis%2C%20Cairo%20Governorate!5e0!3m2!1sen!2seg!4v1705869000000!5m2!1sen!2seg"
                            width="100%"
                            height="450"
                            style={{ border: 0, borderRadius: '8px' }}
                            allowFullScreen=""
                            loading="lazy"
                        ></iframe>
                    </div>
                </Section>
            </main>
        </>
    );
};

export default Contact;
